import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { BookOpen, Clock, CheckCircle, PlayCircle, ArrowRight } from 'lucide-react';

const API = '/api';

export const LearnerDashboard = () => {
  const { user, token } = useAuth();
  const { t, language, direction } = useLanguage();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await fetch(`${API}/learner/enrollments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, progress) => {
    if (status === 'completed') {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    }
    if (progress > 0) {
      return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
    }
    return <Badge variant="outline">Not Started</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="learner-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="container-custom py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#111827] mb-2">
            {language === 'ar' ? 'مرحباً' : 'Welcome back'}, {user?.name || 'Learner'}!
          </h1>
          <p className="text-[#6B7280]">
            {language === 'ar' ? 'تابع تعلمك من حيث توقفت' : 'Continue your learning journey'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container-custom py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#111827]">
            {language === 'ar' ? 'دوراتي' : 'My Courses'}
          </h2>
          <span className="text-[#6B7280]">
            {enrollments.length} {language === 'ar' ? 'دورة' : 'course(s)'}
          </span>
        </div>

        {enrollments.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <BookOpen className="h-16 w-16 text-[#9CA3AF] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#111827] mb-2">
                {language === 'ar' ? 'لم يتم تسجيلك في أي دورة بعد' : 'No courses yet'}
              </h3>
              <p className="text-[#6B7280] mb-6">
                {language === 'ar' 
                  ? 'تواصل معنا للتسجيل في الدورات التدريبية' 
                  : 'Contact us to get enrolled in training courses'}
              </p>
              <Link to="/courses">
                <Button className="btn-primary">
                  {language === 'ar' ? 'تصفح الدورات' : 'Browse Courses'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrollments.map((enrollment) => (
              <Card 
                key={enrollment.enrollment_id} 
                className="card-hover overflow-hidden"
                data-testid={`enrollment-card-${enrollment.enrollment_id}`}
              >
                {/* Course Image */}
                <div className="h-40 bg-gradient-to-br from-[#33AADD] to-[#1e88c9] relative">
                  {enrollment.course?.image_url ? (
                    <img 
                      src={enrollment.course.image_url} 
                      alt={enrollment.course?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-white/50" />
                    </div>
                  )}
                  <div className="absolute top-3 end-3">
                    {getStatusBadge(enrollment.status, enrollment.progress_percent)}
                  </div>
                </div>

                <CardContent className="p-5">
                  <h3 className="font-semibold text-[#111827] mb-2 line-clamp-2">
                    {language === 'ar' 
                      ? enrollment.course?.title_ar 
                      : enrollment.course?.title}
                  </h3>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6B7280]">
                        {language === 'ar' ? 'التقدم' : 'Progress'}
                      </span>
                      <span className="font-medium text-[#111827]">
                        {Math.round(enrollment.progress_percent)}%
                      </span>
                    </div>
                    <Progress value={enrollment.progress_percent} className="h-2" />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-sm text-[#6B7280] mb-4">
                    {enrollment.course?.duration_days && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {enrollment.course.duration_days} {language === 'ar' ? 'أيام' : 'days'}
                      </span>
                    )}
                    {enrollment.status === 'completed' && (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        {language === 'ar' ? 'مكتمل' : 'Completed'}
                      </span>
                    )}
                  </div>

                  {/* Action */}
                  <Link to={`/learner/course/${enrollment.course_id}`}>
                    <Button className="w-full btn-primary" data-testid={`continue-btn-${enrollment.enrollment_id}`}>
                      {enrollment.progress_percent > 0 ? (
                        <>
                          <PlayCircle className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'متابعة' : 'Continue'}
                        </>
                      ) : (
                        <>
                          <PlayCircle className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'ابدأ' : 'Start'}
                        </>
                      )}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearnerDashboard;
